// path: src/Redux/thunks/vaccinationThunks.js
import { createAsyncThunk } from "@reduxjs/toolkit";
import { axiosMedical } from "../../../api/axios";
import ENDPOINTS from "../../../api/endpoints";
import { resolveMedicalScope } from "./medicalScope";

/* ========= Helpers ========= */
const mapCreateBody = (values = {}) => {
  const vaccine = values.vaccine ?? values.label ?? values.name ?? "";
  if (!vaccine.trim()) {
    throw new Error("Vaccine name is required");
  }
  let date = null;
  const year = values.year ?? values.date;
  if (typeof year === "string" && year.trim()) {
    // Accept "YYYY" or full ISO date
    date = /^\d{4}-\d{2}-\d{2}$/.test(year) ? year : `${year}-01-01`;
  } else if (typeof year === "number" && Number.isFinite(year)) {
    date = `${year}-01-01`;
  }
  return { vaccine, date };
};

const mapUpdateBody = (values = {}) => {
  const body = {};
  if (values.vaccine ?? values.label ?? values.name) {
    body.vaccine = values.vaccine ?? values.label ?? values.name;
  }
  if (values.year !== undefined || values.date !== undefined) {
    const year = values.year ?? values.date;
    if (typeof year === "string" && year.trim()) {
      body.date = /^\d{4}-\d{2}-\d{2}$/.test(year) ? year : `${year}-01-01`;
    } else if (typeof year === "number" && Number.isFinite(year)) {
      body.date = `${year}-01-01`;
    }
  }
  return body;
};

/* ========= THUNKS ========= */

export const fetchVaccinations = createAsyncThunk(
  "vaccinations/fetch",
  async (target, { rejectWithValue, getState }) => {
    try {
      const { userId: scopedUserId, headers } = resolveMedicalScope(
        getState(),
        target
      );
      const { data } = await axiosMedical.get(
        ENDPOINTS.MEDICAL.VACCINATIONS(scopedUserId),
        { headers }
      );
      if (!Array.isArray(data)) {
        return rejectWithValue("Format inattendu (array attendu).");
      }
      return data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || err?.message || "Erreur réseau"
      );
    }
  }
);

export const addVaccination = createAsyncThunk(
  "vaccinations/add",
  async ({ userId, values }, { rejectWithValue, getState }) => {
    try {
      const { userId: scopedUserId, headers } = resolveMedicalScope(
        getState(),
        userId
      );
      const body = mapCreateBody(values);
      const { data } = await axiosMedical.post(
        ENDPOINTS.MEDICAL.VACCINATIONS(scopedUserId),
        body,
        {
          headers: {
            "Content-Type": "application/json",
            ...headers,
          },
        }
      );
      return data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || err?.message || "Erreur réseau"
      );
    }
  }
);

export const updateVaccination = createAsyncThunk(
  "vaccinations/update",
  async ({ userId, id, values }, { rejectWithValue, getState }) => {
    try {
      const { userId: scopedUserId, headers } = resolveMedicalScope(
        getState(),
        userId
      );
      const body = mapUpdateBody(values);
      const { data } = await axiosMedical.put(
        ENDPOINTS.MEDICAL.UPDATE_VACCINATION(scopedUserId, id),
        body,
        {
          headers: {
            "Content-Type": "application/json",
            ...headers,
          },
        }
      );
      return data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || err?.message || "Erreur mise à jour"
      );
    }
  }
);

export const removeVaccination = createAsyncThunk(
  "vaccinations/remove",
  async ({ userId, id }, { rejectWithValue, getState }) => {
    try {
      const { userId: scopedUserId, headers } = resolveMedicalScope(
        getState(),
        userId
      );
      await axiosMedical.delete(ENDPOINTS.MEDICAL.DELETE_VACCINATION(scopedUserId, id), {
        headers,
      });
      return id;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || err?.message || "Erreur suppression"
      );
    }
  }
);


